import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { refundOrder } from "../services/refunds";
import { centsToCurrency } from "../utils";

export interface OrderRefundRecord {
  id?: string | number;
  amount?: number;
  currency?: string;
  paymentId?: string;
  status?: string;
  sourceId?: {
    token?: string;
  };
  refund?: {
    id?: string;
    status?: string;
    amount?: number;
    currency?: string;
    createdAt?: string | null;
  };
  refundedAt?: string | { seconds?: number; toDate?: () => Date } | null;
}

interface OrderRefundPanelProps {
  record?: OrderRefundRecord;
  onRefund?: (request: { orderId: string; reason: string }) => Promise<unknown>;
  onRefunded?: () => void;
  onError?: (message: string) => void;
}

const NON_REFUNDABLE_SOURCE_TOKENS = new Set(["CASH", "FREE"]);

export function canRefundOrder(record?: OrderRefundRecord): boolean {
  if (!record?.id) return false;
  if (record.status === "refunded" || record.refund?.id) return false;
  if (!Number.isInteger(record.amount) || Number(record.amount) <= 0) return false;
  if (!record.paymentId || record.paymentId === "demo") return false;

  const sourceToken = String(record.sourceId?.token || "").toUpperCase();
  return !NON_REFUNDABLE_SOURCE_TOKENS.has(sourceToken);
}

export function formatRefundTimestamp(
  timestamp?: string | { seconds?: number; toDate?: () => Date } | null
): string {
  if (!timestamp) return "";
  if (typeof timestamp === "string") {
    return new Date(timestamp).toLocaleString();
  }
  if (typeof timestamp.toDate === "function") {
    return timestamp.toDate().toLocaleString();
  }
  if (typeof timestamp.seconds === "number") {
    return new Date(timestamp.seconds * 1000).toLocaleString();
  }
  return "";
}

function getRefundAmount(record: OrderRefundRecord): string {
  const amount = record.refund?.amount ?? record.amount ?? 0;
  return centsToCurrency(amount);
}

export function OrderRefundPanel({
  record,
  onRefund,
  onRefunded,
  onError,
}: OrderRefundPanelProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);

  if (!record) return null;

  const refundStatus = record.refund?.status;
  const refundId = record.refund?.id;
  const refundedTimestamp =
    formatRefundTimestamp(record.refundedAt) ||
    formatRefundTimestamp(record.refund?.createdAt || null);

  if (record.status === "refunded" || refundId) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Refund
        </Typography>
        <Typography variant="body2">Status: {refundStatus || "refunded"}</Typography>
        {refundId && <Typography variant="body2">Refund ID: {refundId}</Typography>}
        <Typography variant="body2">Amount: {getRefundAmount(record)}</Typography>
        {refundedTimestamp && (
          <Typography variant="body2">Refunded: {refundedTimestamp}</Typography>
        )}
      </Box>
    );
  }

  if (!canRefundOrder(record)) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Refund
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This order is not eligible for a Square refund.
        </Typography>
      </Box>
    );
  }

  const orderId = String(record.id);
  const reason = `Admin full-order refund for order ${orderId}`;

  const handleRefund = async () => {
    setIsRefunding(true);
    try {
      const refund = onRefund || refundOrder;
      await refund({ orderId, reason });
      setIsConfirmOpen(false);
      onRefunded?.();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to refund order.";
      onError?.(message);
    } finally {
      setIsRefunding(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Refund
      </Typography>
      <Button
        color="error"
        disabled={isRefunding}
        startIcon={<CurrencyExchangeIcon />}
        variant="contained"
        onClick={() => setIsConfirmOpen(true)}
      >
        Refund Order
      </Button>
      <Dialog open={isConfirmOpen} onClose={() => setIsConfirmOpen(false)}>
        <DialogTitle>Refund Order</DialogTitle>
        <DialogContent>
          <Typography>
            This will refund the full order amount of{" "}
            {centsToCurrency(record.amount || 0)} through Square and mark
            related passes as refunded.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={isRefunding} onClick={() => setIsConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            disabled={isRefunding}
            onClick={handleRefund}
            variant="contained"
          >
            Refund
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
